import * as d3 from 'd3';
import { select } from 'd3-selection';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import updateLocale from 'dayjs/plugin/updateLocale';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(updateLocale);

dayjs.updateLocale('en', {
  monthsShort: [
    'Jan.', 'Feb.', 'March', 'April', 'May', 'June',
    'July', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.',
  ],
  meridiem: (hour, minute, isLowercase) => (hour >= 12 ? 'p.m.' : 'a.m.'),
});

d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
  ],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ],
  shortMonths: [
    'Jan.', 'Feb.', 'March', 'April', 'May', 'June',
    'July', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.',
  ],
});

// Format value to show max 2 significant digits: 1.13 → "1.1%", 1.00 → "1%"
function formatPct(value) {
  if (value === 0) return '0%';
  const rounded = Math.round(value * 10) / 10;
  if (rounded === Math.floor(rounded)) {
    return `${Math.floor(rounded)}%`;
  }
  return `${rounded}%`;
}

export default function (props, container, isMobile) {
  const { initialWidth, initialHeight } = props;

  const margin = {
    top: 20, right: 20, bottom: 30, left: 40,
  };
  const width = initialWidth - margin.left - margin.right;
  const height = initialHeight - margin.top - margin.bottom;

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', initialHeight)
    .append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`);

  // Use scaleBand for categorical x-axis (years)
  const x = d3.scaleBand().range([0, width]).padding(0.2);
  const y = d3.scaleLinear().range([height, 0]);

  svg.append('g')
    .attr('class', 'x-axis grid')
    .attr('transform', `translate(0,${height})`);

  svg.append('g')
    .attr('class', 'y-axis grid');

  // Create groups for bars, zero labels, and missing labels
  const barsGroup = svg.append('g').attr('class', 'bars-group');
  const zeroLabelsGroup = svg.append('g').attr('class', 'zero-labels-group');
  const missingLabelsGroup = svg.append('g').attr('class', 'missing-labels-group');

  const tooltip = d3.select('body').append('div')
    .attr('class', 'tooltip')
    .style('position', 'absolute')
    .style('background', '#ffffff')
    .style('padding', '5px 10px')
    .style('border', '1px solid #d3d3d3')
    .style('border-radius', '3px')
    .style('pointer-events', 'none')
    .style('opacity', 0);

  const TRANSITION_DURATION = 750;

  d3.json('https://ix.cnn.io/data/vax-exemptions-jama/county_exemption_rates.json')
    .then((rawData) => {
      // Filter for non-medical exemptions and map to chart-friendly format (exclude 2025)
      const countyData = rawData.data
        .filter(d => d.exemption_type === 'nonmed' && d.pct !== null && d.year <= 2024)
        .map(d => ({
          year: String(d.year),
          value: d.pct,
          county: d.county,
          state: d.state,
          state_abb: d.state_abb,
          fips: d.fips_orig,
        }))
        .sort((a, b) => d3.ascending(a.year, b.year));

      // Get all unique years across the dataset for consistent x-axis (exclude 2025)
      const allYears = [...new Set(countyData.map(d => d.year))]
        .filter(year => parseInt(year, 10) <= 2024)
        .sort();

      // Build unique county list with fips as key, including state info and county_type
      const countyList = [...new Map(
        rawData.data
          .filter(d => d.exemption_type === 'nonmed')
          .map(d => [d.fips_orig, {
            fips: d.fips_orig,
            county: d.county,
            state: d.state,
            state_abb: d.state_abb,
            countyType: d.county_type,
            label: d.county,
          }]),
      ).values()].sort((a, b) => a.label.localeCompare(b.label));

      // Build unique state list
      const stateList = [...new Set(countyList.map(c => c.state))].sort();

      // Get DOM elements
      const stateSelect = d3.select('#state-select');
      const countySelect = d3.select('#county-select');
      const countyLabel = d3.select('#county-label');

      // Populate state dropdown
      stateList.forEach((state) => {
        stateSelect.append('option')
          .attr('value', state)
          .text(state);
      });

      // Function to populate county dropdown based on selected state
      function populateCountyDropdown(state) {
        // Clear existing options
        countySelect.selectAll('option').remove();

        // Filter counties for selected state
        const stateCounties = countyList
          .filter(c => c.state === state)
          .sort((a, b) => a.label.localeCompare(b.label));

        // Get unique county types for this state
        const countyTypes = [...new Set(stateCounties.map(c => c.countyType))];

        // Check if this is state-only data
        const isStateOnly = stateCounties.length === 1
          && (countyTypes[0] === 'State' || stateCounties[0].county === 'State');

        if (isStateOnly) {
          // Show state name in dropdown and disable it
          countySelect.append('option')
            .attr('value', stateCounties[0].fips)
            .text(state);
          countySelect.property('disabled', true);
          countyLabel.text('State-level data only:');
        } else {
          // Enable dropdown
          countySelect.property('disabled', false);

          // Update label based on county types present
          if (countyTypes.includes('School District')) {
            countyLabel.text('Select a school district:');
          } else if (countyTypes.includes('Health District')) {
            countyLabel.text('Select a health district:');
          } else {
            countyLabel.text('Select a county:');
          }

          // Add options
          stateCounties.forEach((c) => {
            countySelect.append('option')
              .attr('value', c.fips)
              .text(c.label);
          });
        }

        // Return the first county's fips for initial selection
        return stateCounties.length > 0 ? stateCounties[0].fips : null;
      }

      // Set initial state to California
      const initialState = 'California';
      stateSelect.property('value', initialState);

      // Populate counties for initial state and get first county
      const initialFips = populateCountyDropdown(initialState);
      countySelect.property('value', initialFips);

      // Calculate Y-axis domain for a given dataset (start from 0 for bar charts)
      function calculateYDomain(data) {
        const max = d3.max(data, d => d.value);
        const padding = max * 0.1 || 1;
        return [0, max + padding];
      }

      // Set up x-axis with all years
      x.domain(allYears);

      // On mobile, only show every 5 years to avoid overlap
      const xAxis = d3.axisBottom(x)
        .tickValues(isMobile
          ? allYears.filter(year => parseInt(year, 10) % 5 === 0)
          : allYears);

      svg.select('.x-axis')
        .call(xAxis)
        .selectAll('.tick text')
        .attr('class', 'axis-label');

      // Get initial county data and set up y-axis
      const initialCountyData = countyData.filter(d => d.fips === initialFips);
      const [yMin, yMax] = calculateYDomain(initialCountyData);
      y.domain([yMin, yMax]);

      // Generate tick values using "nice" intervals (0.5, 1, 2, 5, 10, etc.)
      // Aims for ~4-6 ticks, avoids ugly values like 0.2, 0.4, 0.6
      function getYTickValues(domain) {
        const [, max] = domain;
        const targetTicks = 5;
        const rawInterval = max / targetTicks;

        // Choose from nice intervals only
        const niceIntervals = [0.5, 1, 2, 5, 10, 20, 50];
        const interval = niceIntervals.find(n => n >= rawInterval) || niceIntervals[niceIntervals.length - 1];

        const ticks = [];
        for (let v = 0; v <= max; v += interval) {
          // Round to avoid floating point issues
          ticks.push(Math.round(v * 10) / 10);
        }
        return ticks;
      }

      // Y-axis with percentage format, using nice tick intervals
      function createYAxis() {
        return d3.axisLeft(y)
          .tickValues(getYTickValues(y.domain()))
          .tickSize(-width)
          .tickFormat(d => formatPct(d));
      }

      svg.select('.y-axis')
        .call(createYAxis())
        .selectAll('.tick text')
        .attr('class', 'axis-label');

      function updateChart(fips) {
        const filteredData = countyData.filter(d => d.fips === fips);

        // Calculate the new Y-axis domain
        const [yMin, yMax] = calculateYDomain(filteredData);
        y.domain([yMin, yMax]);

        // Update the Y-axis
        svg.select('.y-axis')
          .transition()
          .duration(TRANSITION_DURATION)
          .call(createYAxis());

        // Separate data into bars (value > 0) and zeros
        const barsData = filteredData.filter(d => d.value > 0);
        const zerosData = filteredData.filter(d => d.value === 0);

        // Find missing years (years in allYears but not in this county's data)
        const dataYears = new Set(filteredData.map(d => d.year));
        const missingYears = allYears.filter(year => !dataYears.has(year));

        // Bind data to bars (only non-zero values)
        const bars = barsGroup.selectAll('.bar')
          .data(barsData, d => d.year);

        // Remove old bars
        bars.exit()
          .transition()
          .duration(TRANSITION_DURATION)
          .attr('y', height)
          .attr('height', 0)
          .remove();

        // Add new bars
        const barsEnter = bars.enter()
          .append('rect')
          .attr('class', 'bar')
          .attr('x', d => x(d.year))
          .attr('width', x.bandwidth())
          .attr('y', height)
          .attr('height', 0)
          .attr('fill', '#231151');

        // Helper to position tooltip, flipping to left side if near right edge
        function positionTooltip(event) {
          const tooltipWidth = 120; // approximate tooltip width
          const windowWidth = window.innerWidth;
          const nearRightEdge = event.pageX + tooltipWidth + 20 > windowWidth;

          if (nearRightEdge) {
            tooltip.style('left', `${event.pageX - tooltipWidth - 10}px`);
          } else {
            tooltip.style('left', `${event.pageX + 10}px`);
          }
          tooltip.style('top', `${event.pageY - 30}px`);
        }

        // Update all bars (enter + update)
        barsEnter.merge(bars)
          .on('mouseover', function (event, d) {
            d3.select(this).attr('opacity', 0.8);
            tooltip.transition().duration(100).style('opacity', 1);
            tooltip.html(`${d.year}<br>Exemption rate: ${formatPct(d.value)}`);
            positionTooltip(event);
          })
          .on('mousemove', (event) => {
            positionTooltip(event);
          })
          .on('mouseout', function () {
            d3.select(this).attr('opacity', 1);
            tooltip.transition().duration(100).style('opacity', 0);
          })
          .transition()
          .duration(TRANSITION_DURATION)
          .attr('x', d => x(d.year))
          .attr('width', x.bandwidth())
          .attr('y', d => y(d.value))
          .attr('height', d => height - y(d.value));

        // Handle zero labels
        const zeroLabels = zeroLabelsGroup.selectAll('.zero-label')
          .data(zerosData, d => d.year);

        // Remove old zero labels
        zeroLabels.exit()
          .transition()
          .duration(TRANSITION_DURATION)
          .style('opacity', 0)
          .remove();

        // Add new zero labels
        const zeroLabelsEnter = zeroLabels.enter()
          .append('text')
          .attr('class', 'zero-label')
          .attr('x', d => x(d.year) + x.bandwidth() / 2)
          .attr('y', height - 5)
          .attr('text-anchor', 'middle')
          .attr('fill', '#666')
          .attr('font-size', '11px')
          .style('opacity', 0)
          .text('0');

        // Update all zero labels
        zeroLabelsEnter.merge(zeroLabels)
          .transition()
          .duration(TRANSITION_DURATION)
          .attr('x', d => x(d.year) + x.bandwidth() / 2)
          .attr('y', height - 5)
          .style('opacity', 1);

        // Handle missing data labels (dash for years with no data)
        const missingLabels = missingLabelsGroup.selectAll('.missing-label')
          .data(missingYears, d => d);

        // Remove old missing labels
        missingLabels.exit()
          .transition()
          .duration(TRANSITION_DURATION)
          .style('opacity', 0)
          .remove();

        // Add new missing labels
        const missingLabelsEnter = missingLabels.enter()
          .append('text')
          .attr('class', 'missing-label')
          .attr('x', d => x(d) + x.bandwidth() / 2)
          .attr('y', height - 5)
          .attr('text-anchor', 'middle')
          .attr('fill', '#999')
          .attr('font-size', '12px')
          .style('opacity', 0)
          .text('–');

        // Update all missing labels
        missingLabelsEnter.merge(missingLabels)
          .transition()
          .duration(TRANSITION_DURATION)
          .attr('x', d => x(d) + x.bandwidth() / 2)
          .attr('y', height - 5)
          .style('opacity', 1);
      }

      // Initial chart render
      updateChart(initialFips);

      // Handle state dropdown change
      stateSelect.on('change', function () {
        const selectedState = d3.select(this).property('value');
        const firstCountyFips = populateCountyDropdown(selectedState);
        if (firstCountyFips) {
          countySelect.property('value', firstCountyFips);
          updateChart(firstCountyFips);
        }
      });

      // Handle county dropdown change
      countySelect.on('change', function () {
        const selectedFips = d3.select(this).property('value');
        updateChart(selectedFips);
      });

      const { pymChild } = window;
      pymChild.sendHeight();
    })
    .catch(error => console.error('Error fetching data:', error));
}
